// Copyright (c) 2026 Michael F. Kamprath
//
// canvas.js -- PixiJS application and responsive canvas setup.
//
// Creates a PixiJS Application and scales it to fill the viewport while
// maintaining aspect ratio.  Two design resolutions are supported:
//
//   Standard (4:3)  -- 1024 x 768,  matches legacy iPad backgrounds
//   Wide     (16:9) -- 1136 x 640,  matches legacy iPhone 5 backgrounds
//
// On initial load (before any scene is built), getLayoutVariant() picks
// whichever resolution best matches the viewport.  Once a scene is built,
// the scene calls lockDesignSize() to pin the design resolution so that
// resize() keeps the stage coordinates consistent with the scene content.
// The lock is released when the scene is torn down, allowing the next
// scene to pick whichever variant fits the new viewport size.

import { Application } from 'pixi.js';

/** Standard (4:3) design resolution matching the legacy iPad layout. */
export const STANDARD_WIDTH = 1024;
export const STANDARD_HEIGHT = 768;
const STANDARD_RATIO = STANDARD_WIDTH / STANDARD_HEIGHT; // ~1.333

/** Wide (16:9) design resolution matching the legacy iPhone 5 layout. */
export const WIDE_WIDTH = 1136;
export const WIDE_HEIGHT = 640;
const WIDE_RATIO = WIDE_WIDTH / WIDE_HEIGHT; // ~1.775

/**
 * The midpoint between 4:3 and 16:9 is the crossover where we switch
 * design resolutions.  ~1.554 (roughly 14:9).
 */
const CROSSOVER_RATIO = (STANDARD_RATIO + WIDE_RATIO) / 2;

/**
 * Return which layout variant best fits the current viewport.
 * Call this when deciding which image/position set to load for a new scene.
 *
 * @returns {'standard' | 'wide'}
 */
export function getLayoutVariant() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return (vw / vh) >= CROSSOVER_RATIO ? 'wide' : 'standard';
}

/**
 * Return design dimensions for a given layout variant.
 * @param {'standard' | 'wide'} [variant]  Defaults to getLayoutVariant().
 * @returns {{ width: number, height: number }}
 */
export function getDesignSize(variant) {
  const v = variant ?? getLayoutVariant();
  if (v === 'wide') {
    return { width: WIDE_WIDTH, height: WIDE_HEIGHT };
  }
  return { width: STANDARD_WIDTH, height: STANDARD_HEIGHT };
}

/**
 * Create the PixiJS application and attach it to the parent element.
 *
 * @param {HTMLElement} parent
 * @returns {{ app: Application, container: HTMLElement, initPromise: Promise,
 *             resize: () => void, lockDesignSize: (w: number, h: number) => void,
 *             unlockDesignSize: () => void, destroy: () => void }}
 */
export function createCanvas(parent) {
  if (!(parent instanceof HTMLElement)) {
    throw new TypeError('createCanvas: parent must be an element');
  }

  const container = document.createElement('div');
  container.className = 'canvas-container';
  parent.appendChild(container);

  // When a scene is active, the design size is locked so resize() always
  // uses the same coordinate space the scene was built for.
  let lockedDesignW = null;
  let lockedDesignH = null;

  const app = new Application();
  const initPromise = app.init({
    width: STANDARD_WIDTH,
    height: STANDARD_HEIGHT,
    backgroundColor: 0x000000,
    antialias: true,
    autoDensity: true,
    resolution: window.devicePixelRatio || 1,
  }).then(() => {
    // Enable event propagation on the stage so pointer events reach
    // child display objects (countable sprites with eventMode 'static').
    app.stage.eventMode = 'static';
    container.appendChild(app.canvas);
    resize();
  });

  /**
   * Scale the canvas to fill the container while maintaining the current
   * design resolution's aspect ratio.
   *
   * With autoDensity + resolution set to devicePixelRatio, PixiJS's
   * renderer.resize(w, h) expects CSS pixels: it multiplies internally
   * by the resolution factor for the backing-buffer size and sets
   * canvas.style automatically.
   */
  function resize() {
    if (!app.canvas) return;

    const cw = container.clientWidth || window.innerWidth;
    const ch = container.clientHeight || window.innerHeight;

    // Use locked design size if a scene is active, otherwise pick from viewport.
    let designW, designH;
    if (lockedDesignW != null) {
      designW = lockedDesignW;
      designH = lockedDesignH;
    } else {
      const viewportRatio = cw / ch;
      const variant = viewportRatio >= CROSSOVER_RATIO ? 'wide' : 'standard';
      designW = variant === 'wide' ? WIDE_WIDTH : STANDARD_WIDTH;
      designH = variant === 'wide' ? WIDE_HEIGHT : STANDARD_HEIGHT;
    }

    const designRatio = designW / designH;
    const viewportRatio = cw / ch;

    let canvasWidth, canvasHeight;
    if (viewportRatio > designRatio) {
      // Viewport wider than design -- pillarbox
      canvasHeight = ch;
      canvasWidth = ch * designRatio;
    } else {
      // Viewport taller than design -- letterbox
      canvasWidth = cw;
      canvasHeight = cw / designRatio;
    }

    app.renderer.resize(canvasWidth, canvasHeight);

    const scale = canvasWidth / designW;
    app.stage.scale.set(scale, scale);
  }

  /**
   * Lock the design resolution to fixed dimensions.  Call this when a
   * scene is built so that subsequent resize() calls keep the stage
   * coordinate space consistent with the scene content.
   *
   * @param {number} w  Design width  (e.g. 1024 or 1136)
   * @param {number} h  Design height (e.g.  768 or  640)
   */
  function lockDesignSize(w, h) {
    lockedDesignW = w;
    lockedDesignH = h;
    resize(); // re-run immediately with the new lock
  }

  /**
   * Release the design-size lock.  Call this when the scene is torn down
   * so the next scene can pick whichever variant best fits the viewport.
   */
  function unlockDesignSize() {
    lockedDesignW = null;
    lockedDesignH = null;
  }

  window.addEventListener('resize', resize);

  return {
    app,
    container,
    initPromise,
    resize,
    lockDesignSize,
    unlockDesignSize,
    destroy() {
      window.removeEventListener('resize', resize);
      app.destroy(true, { children: true, texture: true });
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    },
  };
}
